import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Snackbar from '@mui/material/Snackbar';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import DiamondIcon from '@mui/icons-material/Diamond';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PageShell from '../components/PageShell';
import CustomerNav from '../components/CustomerNav';
import request from '../api';
import { formatMXN } from '../numberUtils';

function PackageCard({ pkg, onPurchase, busy }) {
    return (
        <Paper
            sx={{
                p: { xs: 3, sm: 4 },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: 1.5,
            }}
        >
            <DiamondIcon color="primary" sx={{ fontSize: 48 }} />
            <Typography variant="h3" fontWeight={800} color="primary.main">
                {pkg.gems}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>
                {formatMXN(pkg.price)}
            </Typography>
            <Button
                variant="contained"
                size="large"
                fullWidth
                disabled={busy}
                onClick={() => onPurchase(pkg)}
                sx={{ mt: 2 }}
            >
                Comprar
            </Button>
        </Paper>
    );
}

function AvatarCard({ avatar, gems, onPurchase, onEquip, onUnequip, busy }) {
    return (
        <Paper sx={{ p: { xs: 1.5, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center' }}>
            <Avatar src={avatar.image_url || undefined} sx={{ width: { xs: 56, sm: 72 }, height: { xs: 56, sm: 72 } }}>
                {!avatar.image_url && <PersonIcon />}
            </Avatar>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" justifyContent="center">
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>{avatar.name}</Typography>
                {avatar.equipped && (
                    <Chip size="small" icon={<CheckCircleIcon />} label="Equipado" color="success" />
                )}
            </Stack>
            {/* Descripción oculta temporalmente en la vista, sin quitarla del código
            {avatar.description && (
                <Typography variant="body2" color="text.secondary">{avatar.description}</Typography>
            )}
            */}
            <Box sx={{ flexGrow: 1 }} />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <DiamondIcon fontSize="small" color="primary" />
                <Typography fontWeight={700}>{avatar.price_gems}</Typography>
            </Stack>
            {avatar.owned ? (
                avatar.equipped ? (
                    <Button variant="outlined" fullWidth disabled={busy} onClick={() => onUnequip(avatar)}>
                        Quitar
                    </Button>
                ) : (
                    <Button variant="contained" fullWidth disabled={busy} onClick={() => onEquip(avatar)}>
                        Equipar
                    </Button>
                )
            ) : (
                <Button
                    variant="contained"
                    fullWidth
                    disabled={busy || gems < avatar.price_gems}
                    onClick={() => onPurchase(avatar)}
                >
                    Comprar
                </Button>
            )}
        </Paper>
    );
}

function PurchaseGems() {
    const [token] = useState(localStorage.getItem('customerToken'));
    const [customer, setCustomer] = useState(null);
    const [packages, setPackages] = useState([]);
    const [avatars, setAvatars] = useState([]);
    const [tab, setTab] = useState('gems');
    const [busyId, setBusyId] = useState(null);
    const [snackbar, setSnackbar] = useState('');
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const pollingRef = useRef(false);

    const loadMe = useCallback(() => {
        if (!token) return;
        request('/customer/me', { token })
            .then(setCustomer)
            .catch(() => {
                localStorage.removeItem('customerToken');
                navigate('/auctions');
            });
    }, [token, navigate]);

    const loadPackages = useCallback(() => {
        if (!token) return;
        request('/customer/packages', { token }).then(setPackages).catch(() => {});
    }, [token]);

    const loadAvatars = useCallback(() => {
        if (!token) return;
        request('/customer/cosmetics', { token }).then(setAvatars).catch(() => {});
    }, [token]);

    useEffect(() => {
        if (!token) {
            navigate('/auctions');
            return;
        }
        loadMe();
        loadPackages();
        loadAvatars();
    }, [token, navigate, loadMe, loadPackages, loadAvatars]);

    // Mercado Pago redirects here after checkout with ?status=success|failure|pending&ref=...
    // The webhook is what actually credits gems, and it can lag slightly behind the
    // redirect, so poll the order a few times before giving up and telling the user
    // to check back.
    useEffect(() => {
        const status = searchParams.get('status');
        const ref = searchParams.get('ref');
        if (!token || !status || !ref || pollingRef.current) return;
        pollingRef.current = true;

        if (status === 'failure') {
            setSnackbar('El pago no se completó, no se realizó ningún cargo');
            setSearchParams({}, { replace: true });
            return;
        }

        // Mercado Pago appends its own payment id to the back_url on redirect (as
        // payment_id, or collection_id on older integrations) — forwarding it lets the
        // backend confirm the payment directly against Mercado Pago's API right now,
        // rather than waiting on a webhook that needs a public URL to ever arrive.
        const paymentId = searchParams.get('payment_id') || searchParams.get('collection_id');
        const orderUrl = paymentId
            ? `/customer/orders/${ref}?payment_id=${encodeURIComponent(paymentId)}`
            : `/customer/orders/${ref}`;

        let attempts = 0;
        const poll = async () => {
            attempts += 1;
            try {
                const order = await request(orderUrl, { token });
                if (order.status === 'approved') {
                    setSnackbar(`Se agregaron ${order.gems} gemas a tu cuenta`);
                    loadMe();
                    setSearchParams({}, { replace: true });
                    return;
                }
                if (order.status === 'rejected' || order.status === 'cancelled') {
                    setSnackbar('El pago no pudo ser procesado, no se realizó ningún cargo');
                    setSearchParams({}, { replace: true });
                    return;
                }
            } catch (err) {
                // fall through and retry
            }
            if (attempts < 8) {
                setTimeout(poll, 1500);
            } else {
                setSnackbar('Tu pago está siendo procesado, tus gemas aparecerán en breve');
                setSearchParams({}, { replace: true });
            }
        };
        poll();
    }, [token, searchParams, setSearchParams, loadMe]);

    const handlePurchasePackage = async (pkg) => {
        setBusyId(`pkg-${pkg.id}`);
        try {
            const { init_point } = await request(`/customer/packages/${pkg.id}/checkout`, {
                method: 'POST',
                token,
            });
            window.location.href = init_point;
        } catch (err) {
            setSnackbar(err.message);
            setBusyId(null);
        }
    };

    const handlePurchaseAvatar = async (avatar) => {
        setBusyId(`av-${avatar.id}`);
        try {
            const updated = await request(`/customer/cosmetics/${avatar.id}/purchase`, {
                method: 'POST',
                token,
            });
            setCustomer((prev) => ({ ...prev, gems: updated.gems }));
            setSnackbar(`Compraste ${avatar.name}`);
            loadAvatars();
        } catch (err) {
            setSnackbar(err.message);
        } finally {
            setBusyId(null);
        }
    };

    const handleEquip = async (avatar) => {
        setBusyId(`av-${avatar.id}`);
        try {
            await request('/customer/equipped', { method: 'PATCH', token, body: { avatar_id: avatar.id } });
            setSnackbar(`${avatar.name} equipado`);
            loadMe();
            loadAvatars();
        } catch (err) {
            setSnackbar(err.message);
        } finally {
            setBusyId(null);
        }
    };

    const handleUnequip = async (avatar) => {
        setBusyId(`av-${avatar.id}`);
        try {
            await request('/customer/equipped', { method: 'PATCH', token, body: { avatar_id: null } });
            setSnackbar(`${avatar.name} sin equipar`);
            loadMe();
            loadAvatars();
        } catch (err) {
            setSnackbar(err.message);
        } finally {
            setBusyId(null);
        }
    };

    if (!customer) {
        return (
            <PageShell title="Tienda" nav={<CustomerNav />}>
                <Typography color="text.secondary">Cargando...</Typography>
            </PageShell>
        );
    }

    return (
        <PageShell title="Tienda" nav={<CustomerNav />}>
            <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <Chip
                    icon={<DiamondIcon />}
                    label={`${Number(customer.gems).toFixed(0)} gemas disponibles`}
                    color="secondary"
                    variant="outlined"
                />
            </Stack>

            <Paper
                variant="outlined"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.25,
                    px: 3,
                    py: 1.5,
                    mb: 3,
                    textAlign: 'center',
                    borderColor: 'rgba(123, 47, 247, 0.35)',
                    backgroundColor: 'rgba(123, 47, 247, 0.08)',
                }}
            >
                <FavoriteIcon color="primary" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                    Con cada compra de gemas nos ayudas a mantener este sitio <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>100% gratuito y libre de anuncios</Box> para toda la comunidad. ¡Gracias por tu apoyo!
                </Typography>
            </Paper>

            <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="fullWidth" sx={{ mb: 4 }}>
                <Tab label="Comprar Gemas" value="gems" />
                <Tab label="Avatares" value="avatars" />
            </Tabs>

            {tab === 'gems' && (
                <>
                    <Typography color="text.secondary" align="center" sx={{ mb: 3 }}>
                        Compra gemas para desbloquear avatares para tu perfil.
                    </Typography>
                    <Grid container spacing={3} justifyContent="center">
                        {packages.map((pkg) => (
                            <Grid key={pkg.id} item xs={12} sm={6} md={4}>
                                <PackageCard pkg={pkg} onPurchase={handlePurchasePackage} busy={busyId === `pkg-${pkg.id}`} />
                            </Grid>
                        ))}
                        {packages.length === 0 && (
                            <Grid item xs={12}>
                                <Typography color="text.secondary" align="center">
                                    No hay paquetes de gemas disponibles en este momento.
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </>
            )}

            {tab === 'avatars' && (
                <>
                    <Typography color="text.secondary" align="center" sx={{ mb: 3 }}>
                        Compra un avatar y equípalo para que se muestre en tu perfil y en tus pujas.
                    </Typography>
                    <Grid container spacing={{ xs: 1.5, sm: 3 }}>
                        {avatars.map((avatar) => (
                            <Grid key={avatar.id} item xs={6} sm={6} md={4}>
                                <AvatarCard
                                    avatar={avatar}
                                    gems={Number(customer.gems)}
                                    onPurchase={handlePurchaseAvatar}
                                    onEquip={handleEquip}
                                    onUnequip={handleUnequip}
                                    busy={busyId === `av-${avatar.id}`}
                                />
                            </Grid>
                        ))}
                        {avatars.length === 0 && (
                            <Grid item xs={12}>
                                <Typography color="text.secondary" align="center">
                                    No hay avatares disponibles en este momento.
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </>
            )}

            <Snackbar
                open={!!snackbar}
                autoHideDuration={4000}
                onClose={() => setSnackbar('')}
                message={snackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </PageShell>
    );
}

export default PurchaseGems;
